import { useState, useRef, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { Calendar, Ticket, ChartColumnIncreasing, UserCog } from "lucide-react"
import ProductoCarta from "./ProductoCarta"
import ProductoCartaMovil from "./ProductoCartaMovil"
import { useTranslation } from "react-i18next"

const ProductoSeccion = () => {
  const [t, i18n] = useTranslation("global")
  const productos = [
    {
      title: t("productos.title-turnos"),
      description: t("productos.descripcion-turnos"),
      content: t("productos.contenido-turnos"),
      linkText: t("productos.ver-mas"),
      linkNavigation: t("https://rgturnos.com.ar"),
      svg: (
        <Calendar size="64" strokeWidth={1}/>
      ),
    },
    {
      title: t("productos.title-ticketera"),
      description: t("productos.descripcion-ticketera"),
      content: t("productos.contenido-ticketera"),
      linkText: t("productos.ver-mas"),
      linkNavigation: "/productos/tiquetera",
      svg: (
        <Ticket size="72" strokeWidth={1}/>
      ),
    },
    {
      title: t("productos.title-financiera"),
      description: t("productos.descripcion-financiera"),
      content: t("productos.contenido-financiera"),
      linkText: t("productos.ver-mas"),
      linkNavigation: "/productos/financiera",
      svg: (
        <ChartColumnIncreasing size="72" strokeWidth={1}/>
      ),
    },
    {
      title: t("productos.title-personalizado"),
      description: t("productos.descripcion-personalizado"),
      content: t("productos.contenido-personalizado"),
      linkText: t("productos.ver-mas"),
      linkNavigation: "/productos/personalizados",
      svg: (
        <UserCog size="72" strokeWidth={1}/>
      ),
    },
  ]

  const [currentIndex, setCurrentIndex] = useState(0)
  const [direction, setDirection] = useState(1)
  const [isMobile, setIsMobile] = useState(false)
  const carouselRef = useRef(null)
  const touchStartX = useRef(null)

  // Check for mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024)
    }

    checkMobile()
    window.addEventListener("resize", checkMobile)
    return () => window.removeEventListener("resize", checkMobile)
  }, [])

  // Touch and swipe handling for mobile
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX
  }

  const handleTouchEnd = (e) => {
    if (!touchStartX.current) return

    const touchEndX = e.changedTouches[0].clientX
    const diffX = touchStartX.current - touchEndX

    if (Math.abs(diffX) > 50) {
      if (diffX > 0) {
        handleNext()
      } else {
        handlePrev()
      }
    }

    touchStartX.current = null
  }

  const handleNext = () => {
    setDirection(1)
    setCurrentIndex((prevIndex) => (prevIndex === productos.length - 1 ? 0 : prevIndex + 1))
  }

  const handlePrev = () => {
    setDirection(-1)
    setCurrentIndex((prevIndex) => (prevIndex === 0 ? productos.length - 1 : prevIndex - 1))
  }

  const slideVariants = {
    initial: (direction) => ({
      x: direction > 0 ? "150%" : "-150%",
      scale: 0.8,
      opacity: 0,
    }),
    animate: {
      x: 0,
      scale: 1,
      opacity: 1,
      transition: {
        type: "tween",
        duration: 0.4,
      },
    },
    exit: (direction) => ({
      x: direction > 0 ? "-150%" : "150%",
      scale: 0.8,
      opacity: 0,
      transition: {
        type: "tween",
        duration: 0.4,
      },
    }),
  }

  return (
    <div id="productos" className="min-h-screen flex flex-col justify-center px-4 py-16 my-20 font-kanit">
      <div className="w-full max-w-6xl ">
        <h2 className="text-xl md:text-4xl mb-2 text-white font-messina tracking-wide text-left">
          {t("productos.title")}
        </h2>
                <div className="h-1 w-20 bg-gradient-to-r from-gray-500 to-transparent mb-10"></div>
      </div>

      {/* Mobile Slider */}
      <div
        className="w-full max-w-6xl flex items-center justify-center sm:space-x-8 lg:hidden relative"
        ref={carouselRef}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <button
          onClick={handlePrev}
          className="text-white hidden sm:flex p-2 rounded-full bg-black/50 hover:bg-black transition z-1"
        >
          <ChevronLeft size={32} />
        </button>

        <div className="flex flex-col items-center justify-center w-full gap-5">
          <div className="relative w-[18rem] h-[25rem] overflow-hidden">
            <AnimatePresence custom={direction} mode="wait">
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={slideVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                className="absolute w-full h-full"
              >
                <ProductoCartaMovil
                  title={productos[currentIndex].title}
                  description={productos[currentIndex].description}
                  linkText={productos[currentIndex].linkText}
                  linkNavigation={productos[currentIndex].linkNavigation}
                  svg={productos[currentIndex].svg}
                />
              </motion.div>
            </AnimatePresence>
          </div>
          <div className="flex justify-center space-x-2 mt-4">
            {[...productos].map((_, index) => (
              <div
                key={index}
                className={`w-3 h-3 rounded-full border-2 border-white 
                  ${index === currentIndex ? "bg-white" : "bg-transparent"}`}
              />
            ))}
          </div>
        </div>

        <button
          onClick={handleNext}
          className="text-white p-2 hidden sm:flex rounded-full bg-black/50 hover:bg-black transition z-1"
        >
          <ChevronRight size={32} />
        </button>
      </div>

      {/* Desktop Grid */}
      <div className="hidden lg:grid grid-cols-3 xl:grid-cols-4 gap-10 w-full mx-auto">
        {productos.map((producto, index) => (
          <ProductoCarta
            key={index}
            title={producto.title}
            description={producto.description}
            content={producto.content}
            linkText={producto.linkText}
            linkNavigation={producto.linkNavigation}
            svg={producto.svg}
          />
        ))}
      </div>
      <div className="w-full max-w-6xl mb-10"></div>
    </div>
  )
}

export default ProductoSeccion
